import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import { Box, Card, Typography, Button, CircularProgress, Alert } from "@mui/material";
import { motion } from "framer-motion";
import { checkUserStatus } from "../services/authService";

export default function SubscriptionSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const [paymentIntent, setPaymentIntent] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'ready' | 'delayed' | 'error'>('checking');

  // Recupero parametri Stripe dal redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pi = params.get("payment_intent");
    const s = params.get("redirect_status");

    console.log("paymentIntent: " + JSON.stringify(pi));

    setPaymentIntent(pi);
    setStatus(s);

    // Simulo elaborazione 1.2s (effetto più elegante)
    setTimeout(() => setLoading(false), 1200);
  }, [location.search]);

  // Controllo attivazione backend
  useEffect(() => {
    if (!status || status !== 'succeeded') return;

    const checkActivation = async () => {
      const maxAttempts = 10; // 30 secondi totali
      const delayMs = 3000; // 3 secondi tra tentativi
      
      console.log(`🔄 Inizio polling attivazione (${maxAttempts} tentativi ogni ${delayMs}ms)`);
      
      for (let i = 0; i < maxAttempts; i++) {
        console.log(`📡 Tentativo ${i + 1}/${maxAttempts}...`);
        
        try {
          const data = await checkUserStatus();
          console.log(`📊 Risposta tentativo ${i + 1}:`, data);
          
          if (data && data.hasAccess) {
            console.log("✅ Utente attivato nel backend!");
            setBackendStatus('ready');
            return; // Esci dalla funzione, non solo dal loop
          } else {
            console.warn(`⏳ Tentativo ${i + 1}: utente non ancora attivo`);
          }
        } catch (err) {
          console.error(`❌ Tentativo ${i + 1} fallito:`, err);
          // Continua il loop
        }
        
        // Attendi prima del prossimo tentativo (non dopo l'ultimo)
        if (i < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      
      // Dopo tutti i tentativi, mostra warning
      console.warn(`⚠️ Attivazione backend non confermata dopo ${maxAttempts} tentativi`);
      setBackendStatus('delayed');
    };

    checkActivation();
  }, [status]);

  const isSuccess = status === "succeeded";

  // 👉 Scarica PDF della ricevuta
  const handleDownloadReceipt = async () => {
    if (!paymentIntent) return;

    try {
      const res = await fetch("/api/stripe/receipt?payment_intent=" + paymentIntent);
      if (!res.ok) throw new Error("Errore recupero ricevuta");

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `ricevuta-${paymentIntent}.pdf`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Errore download PDF", err);
      alert("Impossibile scaricare la ricevuta.");
    }
  };

  return (
    <Box
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "linear-gradient(135deg, #e3e8ef 0%, #f8fafc 100%)",
        padding: 2,
      }}
    >
      {/* LOADER ANIMATO */}
      {loading ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Card
            elevation={6}
            sx={{
              padding: 6,
              borderRadius: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              width: 420,
            }}
          >
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h5" fontWeight="bold">
              Elaborazione in corso…
            </Typography>
            <Typography color="text.secondary" mt={1}>
              Attendere qualche secondo mentre verifichiamo il pagamento.
            </Typography>
          </Card>
        </motion.div>
      ) : (
        // CARD RISULTATO
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 480 }}
        >
          <Card
            elevation={8}
            sx={{
              padding: 4,
              borderRadius: 4,
              background: "white",
              textAlign: "center",
            }}
          >
            {/* ICONA */}
            {isSuccess ? (
              <CheckCircleIcon sx={{ fontSize: 80, color: "#3BB273" }} />
            ) : (
              <ErrorIcon sx={{ fontSize: 80, color: "#D32F2F" }} />
            )}

            {/* TITOLO */}
            <Typography variant="h4" fontWeight="bold" mt={2}>
              {isSuccess ? "Abbonamento Attivato!" : "Pagamento Non Riuscito"}
            </Typography>

            {/* DESCRIZIONE */}
            <Typography
              variant="body1"
              color="text.secondary"
              mt={1}
              mb={3}
              sx={{ maxWidth: 350, margin: "0 auto" }}
            >
              {isSuccess
                ? "🎉 Il tuo abbonamento è stato attivato correttamente. Grazie per la tua fiducia!"
                : "⚠️ Il pagamento non è andato a buon fine. Puoi riprovare o cambiare metodo di pagamento."}
            </Typography>

            {/* STATO BACKEND */}
            {isSuccess && (
              <Box sx={{ mb: 3, width: '100%' }}>
                {backendStatus === 'checking' && (
                  <Alert severity="info" icon={<CircularProgress size={20} />}>
                    <strong>Attivazione in corso...</strong>
                    <Typography variant="body2">
                      Stiamo completando l'attivazione del tuo account.
                    </Typography>
                  </Alert>
                )}
                
                {backendStatus === 'ready' && (
                  <Alert severity="success">
                    <strong>Account attivato!</strong>
                    <Typography variant="body2">
                      Puoi ora accedere a tutti i servizi della piattaforma.
                    </Typography>
                  </Alert>
                )}
                
                {backendStatus === 'delayed' && (
                  <Alert severity="warning" icon={<WarningIcon />}>
                    <strong>Attivazione in elaborazione</strong>
                    <Typography variant="body2">
                      Il pagamento è confermato, ma l'attivazione potrebbe richiedere alcuni minuti.
                      Riceverai un'email di conferma appena completata.
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {/* BOX INFO */}
            <Box
              sx={{
                background: "#f5f6f8",
                padding: 2,
                borderRadius: 3,
                textAlign: "left",
                mb: 3,
                border: "1px solid #e1e4e8",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Payment Intent:</strong> {paymentIntent}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {isSuccess ? "Completato" : status}
              </Typography>
            </Box>

            {/* BOTTONI */}
            <Box display="flex" flexDirection="column" gap={2}>

              
              {/*isSuccess && (
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadReceipt}
                >
                  Scarica ricevuta PDF
                </Button>
              )*/}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={() => navigate("/")}
              >
                Vai alla Home Page
              </Button>
            </Box>
          </Card>
        </motion.div>
      )}
    </Box>
  );
}
